import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Trash2, Ban, CheckSquare, Square as SquareIcon, AlertCircle, Film, Music, Podcast, User, Image as ImageIcon, Newspaper, Video, Users as UsersIcon, Shield, Download, ShieldAlert, BookOpen, MailWarning, Send, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { getMediaUrl } from '../utils/mediaUrl';
import AIModerationReports from './AIModerationReports';
import EmailVerificationModeration from './EmailVerificationModeration';
import FSSModerationPanel from './FSSModerationPanel';
import SystemGroupsModeration from './SystemGroupsModeration';

const PROTECTED = ['contact@destine.studio', 'contact.destinestudio@gmail.com'];

const ModerationPanel = () => {
  const [activeTab, setActiveTab] = useState('posts');
  const [searchQuery, setSearchQuery] = useState('');
  const [content, setContent] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    // AI Moderation tab is fully self-contained — skip the generic content loader.
    if (activeTab === 'ai_mod' || activeTab === 'system_groups') {
      setLoading(false);
      return;
    }
    loadContent();
  }, [activeTab]);

  const loadContent = async () => {
    setLoading(true);
    try {
      let endpoint = '';
      switch (activeTab) {
        case 'posts':
          endpoint = '/api/admin/content/posts';
          break;
        case 'podcasts':
          endpoint = '/api/admin/content/podcasts';
          break;
        case 'photos':
          endpoint = '/api/admin/photos?limit=100';
          break;
        case 'videos':
          endpoint = '/api/admin/videos?limit=100';
          break;
        case 'articles':
          endpoint = '/api/news/feed';
          break;
        case 'freefilms':
          endpoint = '/api/explorer/free-films';
          break;
        case 'shortfilms':
          endpoint = '/api/explorer/videos';
          break;
        case 'films':
          endpoint = '/api/admin/films';
          break;
        case 'popculture':
          endpoint = '/api/admin/pop-culture';
          break;
        case 'bestfilms':
          endpoint = '/api/admin/best-films';
          break;
        case 'boxoffice':
          endpoint = '/api/admin/box-office';
          break;
        case 'ads':
          endpoint = '/api/admin/ads-history';
          break;
        case 'users':
          endpoint = '/api/admin/users';
          break;
        case 'groups':
          endpoint = '/api/admin/groups';
          break;
        case 'edepots':
          endpoint = '/api/edepots/admin/all';
          break;
        default:
          break;
      }
      
      // Skip loading for tabs that have their own panels
      if (['email_unverified', 'email_blocked', 'fss', 'ai_mod'].includes(activeTab)) {
        setLoading(false);
        return;
      }

      const response = await axios.get(endpoint);
      if (activeTab === 'users') {
        // Sort users alphabetically by name
        const sortedUsers = response.data.sort((a, b) => 
          (a.name || '').localeCompare(b.name || '')
        );
        setUsers(sortedUsers);
      } else if (activeTab === 'edepots') {
        // Backend wraps it in {deposits:[...]}
        setContent(response.data?.deposits || []);
      } else if (activeTab === 'photos' || activeTab === 'videos') {
        setContent(response.data?.items || []);
      } else {
        setContent(response.data);
      }
    } catch (error) {
      console.error('Error loading content:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (itemId, type) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contenu ?')) return;

    try {
      let endpoint = '';
      switch (type) {
        case 'group':
          endpoint = `/api/admin/groups/${itemId}`;
          break;
        case 'post':
          endpoint = `/api/admin/content/posts/${itemId}`;
          break;
        case 'photo':
          endpoint = `/api/admin/photos/${itemId}`;
          break;
        case 'video':
          endpoint = `/api/admin/videos/${itemId}`;
          break;
        case 'podcast':
          endpoint = `/api/admin/content/podcasts/${itemId}`;
          break;
        case 'article':
          endpoint = `/api/news/${itemId}/hide`;
          break;
        case 'freefilm':
          endpoint = `/api/explorer/free-films/${itemId}`;
          break;
        case 'shortfilm':
          endpoint = `/api/explorer/videos/${itemId}`;
          break;
        case 'film':
          endpoint = `/api/admin/films/${itemId}`;
          break;
        case 'popculture':
          endpoint = `/api/admin/pop-culture/${itemId}`;
          break;
        case 'bestfilm':
          endpoint = `/api/admin/best-films/${itemId}`;
          break;
        case 'boxoffice':
          endpoint = `/api/admin/box-office/${itemId}`;
          break;
        case 'ad':
          endpoint = `/api/pro/advertisements/${itemId}`;
          break;
        default:
          break;
      }
      
      await axios.delete(endpoint);
      toast.success('Contenu supprimé');
      loadContent();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Erreur');
    }
  };

  const handleBlockUser = (user) => {
    setSelectedUser(user);
    setShowBlockModal(true);
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir SUPPRIMER DÉFINITIVEMENT le compte de ${userName} ? Cette action est irréversible.`)) return;

    try {
      await axios.delete(`/api/admin/users/${userId}`);
      try {
        await axios.post('/api/admin/moderation-log/log', {
          target_type: 'user', target_id: userId, target_title: userName, action: 'deleted',
        });
      } catch (_) { /* ignore */ }
      toast.success('Compte supprimé définitivement');
      loadContent();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const toggleSelect = (id) => setSelectedIds(ids => (ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]));

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`SUPPRIMER DÉFINITIVEMENT ${selectedIds.length} compte(s) ? Cette action est irréversible.`)) return;
    setBulkDeleting(true);
    try {
      const r = await axios.post('/api/admin/users/bulk-delete', { user_ids: selectedIds });
      toast.success(`${r.data?.deleted ?? 0} compte(s) supprimé(s)${r.data?.skipped?.length ? ` · ${r.data.skipped.length} protégé(s) ignoré(s)` : ''}`);
      setSelectedIds([]);
      loadContent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression groupée');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Map activeTab to correct delete type
  const getDeleteType = (tab) => {
    const typeMap = {
      'posts': 'post',
      'photos': 'photo',
      'videos': 'video',
      'podcasts': 'podcast',
      'articles': 'article',
      'freefilms': 'freefilm',
      'shortfilms': 'shortfilm',
      'films': 'film',
      'popculture': 'popculture',
      'bestfilms': 'bestfilm',
      'boxoffice': 'boxoffice',
      'ads': 'ad',
      'groups': 'group'
    };
    return typeMap[tab] || tab;
  };

  const filteredContent = (Array.isArray(content) ? content : []).filter(item => {
    const searchLower = searchQuery.toLowerCase();
    return (
      item.title?.toLowerCase().includes(searchLower) ||
      item.content?.toLowerCase().includes(searchLower) ||
      item.name?.toLowerCase().includes(searchLower) ||
      item.user?.name?.toLowerCase().includes(searchLower)
    );
  });

  const filteredUsers = (Array.isArray(users) ? users : []).filter(user => {
    const searchLower = searchQuery.toLowerCase();
    return (
      user.name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Modération</h2>
        
        {/* Search */}
        <div className="relative w-96">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher..."
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: 'posts', label: 'Gim Toli', icon: Music },
          { id: 'photos', label: 'Photos partagées', icon: ImageIcon },
          { id: 'videos', label: 'Vidéos partagées', icon: Video },
          { id: 'podcasts', label: 'Podcasts', icon: Podcast },
          { id: 'articles', label: 'Actus', icon: Newspaper },
          { id: 'freefilms', label: 'Films Libres', icon: Film },
          { id: 'films', label: 'Sorties Cinéma', icon: Film },
          { id: 'popculture', label: 'Pop Culture', icon: ImageIcon },
          { id: 'bestfilms', label: 'Meilleurs Films', icon: Film },
          { id: 'boxoffice', label: 'Box Office', icon: Film },
          { id: 'ads', label: 'Publicités', icon: ImageIcon },
          { id: 'groups', label: 'Groupes', icon: UsersIcon },
          { id: 'system_groups', label: 'GPT Guyane / GPT Africa', icon: UsersIcon },
          { id: 'edepots', label: 'e-Dépôts', icon: Shield },
          { id: 'fss', label: 'Franchises / Studios / Streaming', icon: Building2 },
          { id: 'ai_mod', label: 'IA — Auto-modération', icon: ShieldAlert },
          { id: 'users', label: 'Utilisateurs', icon: User },
          { id: 'email_unverified', label: 'Emails non vérifiés', icon: MailWarning },
          { id: 'email_blocked', label: 'Comptes bloqués (email)', icon: Ban }
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full font-semibold transition-colors flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'bg-purple-500 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
              data-testid={`moderation-tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      {activeTab === 'email_unverified' ? (
        <EmailVerificationModeration scope="unverified" />
      ) : activeTab === 'email_blocked' ? (
        <EmailVerificationModeration scope="blocked" />
      ) : activeTab === 'ai_mod' ? (
        <AIModerationReports />
      ) : activeTab === 'fss' ? (
        <FSSModerationPanel />
      ) : activeTab === 'system_groups' ? (
        <SystemGroupsModeration />
      ) : loading ? (
        <div className="text-center py-12 text-gray-400">Chargement...</div>
      ) : activeTab === 'users' ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-xl bg-white/5 border border-white/10" data-testid="users-bulk-bar">
            <button
              onClick={() => setSelectedIds(selectedIds.length === filteredUsers.length ? [] : filteredUsers.filter(u => !PROTECTED.includes(u.email)).map(u => u.id))}
              className="flex items-center gap-2 text-sm text-gray-300 hover:text-white"
              data-testid="users-select-all"
            >
              {selectedIds.length > 0 && selectedIds.length === filteredUsers.filter(u => !PROTECTED.includes(u.email)).length ? <CheckSquare className="w-5 h-5 text-purple-400" /> : <SquareIcon className="w-5 h-5" />}
              Tout sélectionner ({filteredUsers.length})
            </button>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400" data-testid="users-selected-count">{selectedIds.length} sélectionné(s)</span>
              <button
                onClick={handleBulkDelete}
                disabled={selectedIds.length === 0 || bulkDeleting}
                className="px-4 py-2 rounded-full bg-red-500/20 text-red-300 hover:bg-red-500/30 disabled:opacity-40 text-sm font-semibold flex items-center gap-2"
                data-testid="users-bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4" /> {bulkDeleting ? 'Suppression…' : `Supprimer la sélection`}
              </button>
            </div>
          </div>
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-gray-400">Aucun utilisateur trouvé</div>
          ) : (
            filteredUsers.map(user => (
              <div key={user.id} className={`bg-white/5 border rounded-xl p-4 flex items-center justify-between ${selectedIds.includes(user.id) ? 'border-purple-500/60 bg-purple-500/10' : 'border-white/10'}`} data-testid={`mod-user-${user.id}`}>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => toggleSelect(user.id)}
                    disabled={PROTECTED.includes(user.email)}
                    className="p-1 text-gray-400 hover:text-white disabled:opacity-30"
                    data-testid={`mod-user-select-${user.id}`}
                  >
                    {selectedIds.includes(user.id) ? <CheckSquare className="w-5 h-5 text-purple-400" /> : <SquareIcon className="w-5 h-5" />}
                  </button>
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center">
                    <span className="font-bold">{user.name?.charAt(0).toUpperCase()}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold">{user.name}</h3>
                    <p className="text-sm text-gray-400">{user.email}</p>
                    {user.is_blocked && (
                      <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded-full">
                        Bloqué
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleBlockUser(user)}
                    className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center space-x-2 ${
                      user.is_blocked
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                        : 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
                    }`}
                    disabled={PROTECTED.includes(user.email)}
                  >
                    <Ban className="w-4 h-4" />
                    <span>{user.is_blocked ? 'Débloquer' : 'Bloquer'}</span>
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user.id, user.name)}
                    className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-full transition-colors"
                    title="Supprimer définitivement"
                    disabled={PROTECTED.includes(user.email)}
                    data-testid={`mod-user-delete-${user.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      ) : activeTab === 'groups' ? (
        <div className="space-y-3">
          {filteredContent.length === 0 ? (
            <div className="text-center py-12 text-gray-400">Aucun groupe trouvé</div>
          ) : (
            filteredContent.map(g => (
              <div key={g.id} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between" data-testid={`admin-group-${g.id}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-12 h-12 rounded-full bg-violet-700/30 overflow-hidden flex items-center justify-center shrink-0">
                    {g.profile_image_url ? (
                      <img src={getMediaUrl(g.profile_image_url)} alt={g.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <UsersIcon className="w-6 h-6 text-violet-300" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold truncate">{g.name}</h3>
                    <p className="text-xs text-gray-400 truncate">{g.theme}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px] text-gray-500">
                      <span>👥 {g.members_count}</span>
                      <span>💬 {g.messages_count}</span>
                      {g.pending_reports > 0 && (
                        <span className="text-amber-400 font-semibold">🚩 {g.pending_reports} signalement{g.pending_reports > 1 ? 's' : ''}</span>
                      )}
                      <span>· créé par {g.creator?.name || g.creator?.username || '?'}</span>
                      <span>· {g.created_at?.slice(0, 10)}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(g.id, 'group')}
                  className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-full transition-colors shrink-0"
                  title="Supprimer le groupe"
                  data-testid={`delete-group-${g.id}`}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))
          )}
        </div>
      ) : activeTab === 'edepots' ? (
        <div className="space-y-3">
          {filteredContent.length === 0 ? (
            <div className="text-center py-12 text-gray-400">Aucun e-dépôt trouvé</div>
          ) : (
            filteredContent.map(d => (
              <div key={d.id} className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid={`admin-edepot-${d.id}`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate flex items-center gap-2">
                      <Shield className="w-4 h-4 text-fuchsia-400 shrink-0" />
                      {d.title}
                    </h3>
                    <p className="text-xs text-fuchsia-300 mt-0.5">{d.work_type}</p>
                  </div>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${
                    d.status === 'paid'
                      ? 'bg-green-500/20 text-green-300'
                      : 'bg-amber-500/20 text-amber-300'
                  }`}>{d.status}</span>
                </div>
                <div className="text-xs text-gray-400 space-y-0.5 mb-3">
                  <p>Déposé par: <span className="text-gray-200">{d.user?.name || d.user?.username || d.user?.email || d.user_id}</span></p>
                  <p>Hash SHA-256: <span className="font-mono text-[10px]">{(d.file_hash_sha256 || '').slice(0, 24)}…</span></p>
                  <p>{d.file_name} · {(d.file_size / 1024 / 1024).toFixed(2)} MB · {d.duration_years} ans</p>
                  {d.deposited_at && <p>Déposé le: {new Date(d.deposited_at).toLocaleString('fr-FR')}</p>}
                </div>
                <div className="flex gap-2">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/edepots/${d.id}/file`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex-1 text-center px-3 py-2 text-xs bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-300 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                    data-testid={`admin-edepot-view-${d.id}`}
                  >
                    <Video className="w-3.5 h-3.5" /> Voir l'œuvre
                  </a>
                  {d.status === 'paid' && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/edepots/${d.id}/certificate`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex-1 text-center px-3 py-2 text-xs bg-green-500/20 hover:bg-green-500/30 text-green-300 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                      data-testid={`admin-edepot-cert-${d.id}`}
                    >
                      <Download className="w-3.5 h-3.5" /> Attestation
                    </a>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      ) : activeTab === 'videos' ? (
        <div className="space-y-3" data-testid="mod-videos-list">
          {filteredContent.length === 0 ? (
            <div className="text-center py-12 text-gray-400">Aucune vidéo partagée</div>
          ) : (
            filteredContent.map(item => (
              <div key={item.id} className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid={`mod-video-${item.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm truncate">{item.user?.name || item.user?.username || item.user_id}</h3>
                    <p className="text-xs text-gray-500">{item.created_at ? new Date(item.created_at).toLocaleString() : ''} · ⏱ {Math.floor((item.duration || 0) / 60)}:{String(Math.floor((item.duration || 0) % 60)).padStart(2, '0')} · ❤️ {item.likes_count || 0} · 💬 {item.comments_count || 0} · 👁 {item.views_count || 0}{item.processing ? ' · ⏳ traitement' : ''}{item.failed ? ' · ❌ échec' : ''}{item.flagged ? ' · 🚩 masquée' : ''}</p>
                  </div>
                  <button
                    onClick={() => handleDelete(item.id, 'video')}
                    className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-full transition-colors shrink-0"
                    title="Supprimer"
                    data-testid={`mod-video-delete-${item.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                {item.description && <p className="text-xs text-gray-300 mb-2 line-clamp-2">{item.description}</p>}
                {item.video_url && (
                  <video src={getMediaUrl(item.video_url)} poster={item.thumbnail_url ? getMediaUrl(item.thumbnail_url) : undefined} controls playsInline preload="metadata" className="w-full max-h-64 rounded-lg bg-black" />
                )}
              </div>
            ))
          )}
        </div>
      ) : activeTab === 'photos' ? (
        <div className="space-y-3">
          {filteredContent.length === 0 ? (
            <div className="text-center py-12 text-gray-400">Aucune photo partagée</div>
          ) : (
            filteredContent.map(item => (
              <div key={item.id} className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid={`mod-photo-${item.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm truncate">{item.user?.name || item.user?.username || item.user_id}</h3>
                    <p className="text-xs text-gray-500">{item.created_at ? new Date(item.created_at).toLocaleString() : ''} · ❤️ {item.likes_count || 0} · 💬 {item.comments_count || 0}</p>
                  </div>
                  <button
                    onClick={() => handleDelete(item.id, 'photo')}
                    className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-full transition-colors shrink-0"
                    title="Supprimer"
                    data-testid={`mod-photo-delete-${item.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                {item.caption && <p className="text-xs text-gray-300 mb-2 line-clamp-2">{item.caption}</p>}
                <div className="flex gap-2 overflow-x-auto">
                  {(item.image_urls || []).map((url, idx) => (
                    <img key={idx} src={getMediaUrl(url)} alt="" className="w-16 h-16 object-cover rounded-lg shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredContent.length === 0 ? (
            <div className="text-center py-12 text-gray-400">Aucun contenu trouvé</div>
          ) : (
            filteredContent.map(item => (
              <div key={item.id} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold">{item.title || item.name || 'Sans titre'}</h3>
                  {/* Ads special rendering */}
                  {activeTab === 'ads' && (
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="text-sm text-gray-400">Par: {item.advertiser_name || 'Inconnu'}</span>
                      <span className="text-xs text-gray-500">|</span>
                      <span className="text-sm text-gray-400">{item.amount ? `${item.amount}€` : 'Gratuit'}</span>
                      <span className="text-xs text-gray-500">|</span>
                      <span className="text-sm text-gray-400">{item.created_at ? new Date(item.created_at).toLocaleDateString('fr-FR') : '?'}</span>
                      <span className="text-xs text-gray-500">|</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${item.is_expired ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                        {item.is_expired ? 'Expirée' : 'Active'}
                      </span>
                      <span className="text-xs text-gray-500">|</span>
                      <span className="text-xs text-gray-400">{item.impressions_count || 0} impr.</span>
                    </div>
                  )}
                  {/* Normal content rendering */}
                  {activeTab !== 'ads' && item.user && (
                    <p className="text-sm text-gray-400">Par: {item.user.name || item.user.email}</p>
                  )}
                  {item.release_date && (
                    <p className="text-xs text-gray-500">
                      Sortie: {new Date(item.release_date).toLocaleDateString('fr-FR')}
                      {new Date(item.release_date) < new Date() && (
                        <span className="ml-2 text-orange-400">(Date dépassée)</span>
                      )}
                    </p>
                  )}
                  {item.content && (
                    <p className="text-sm text-gray-500 truncate mt-1">{item.content}</p>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(item.id, getDeleteType(activeTab))}
                  className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-full transition-colors"
                  title="Supprimer"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Block User Modal */}
      {showBlockModal && selectedUser && (
        <BlockUserModal
          user={selectedUser}
          onClose={() => {
            setShowBlockModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setShowBlockModal(false);
            setSelectedUser(null);
            loadContent();
          }}
        />
      )}
    </div>
  );
};

const BlockUserModal = ({ user, onClose, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [blocking, setBlocking] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user.is_blocked && !reason.trim()) {
      toast.error('Veuillez préciser une raison');
      return;
    }

    setBlocking(true);

    try {
      await axios.post(`/api/admin/users/${user.id}/block`, {
        is_blocked: !user.is_blocked,
        block_reason: reason
      });
      
      toast.success(user.is_blocked ? 'Utilisateur débloqué' : 'Utilisateur bloqué');
      onSuccess();
    } catch (error) {
      console.error('Error blocking user:', error);
      toast.error('Erreur');
    } finally {
      setBlocking(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-md w-full">
        <div className="p-6">
          <h3 className="text-xl font-bold mb-4">
            {user.is_blocked ? 'Débloquer' : 'Bloquer'} {user.name}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!user.is_blocked && (
              <div>
                <label className="block text-sm font-semibold mb-2">Raison du blocage *</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Expliquez pourquoi vous bloquez cet utilisateur..."
                  rows={4}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-red-500 resize-none"
                  required
                />
              </div>
            )}

            {user.is_blocked && user.block_reason && (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-sm text-red-400"><strong>Raison du blocage:</strong></p>
                <p className="text-sm text-gray-300 mt-1">{user.block_reason}</p>
              </div>
            )}

            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 rounded-full font-semibold transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={blocking}
                className={`flex-1 px-6 py-3 rounded-full font-semibold transition-colors disabled:opacity-50 ${
                  user.is_blocked
                    ? 'bg-green-500 hover:bg-green-600'
                    : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {blocking ? 'Traitement...' : (user.is_blocked ? 'Débloquer' : 'Bloquer')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ModerationPanel;
